import React from 'react';
import {
  ChevronDown,
  EllipsisVertical,
  Repeat2,
} from 'lucide-react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {RootState} from '../../store';
import {notificationApi} from '../../services/notificationApi';
import type {NotificationReactionType, NotificationRecord} from '../../services/notificationApi';
import {getAssetUrl, getDisplayInitial} from '../../utils/assets';
import {setNotificationUnreadCount} from '../../store/slices/notificationSlice';
import {useRealtime} from '../../realtime/RealtimeProvider';
import styles from './notifications/Notifications.module.css';
import './Main.css';

type NotificationView = 'all' | 'unread' | 'comments' | 'activity' | 'messages';
type NotificationSortOrder = 'newest' | 'oldest';
type NotificationDateGroup = 'Today' | 'Yesterday' | 'Earlier this week' | 'Older';

const NOTIFICATION_VIEWS: Array<{label: string; value: NotificationView}> = [
  {label: 'All', value: 'all'},
  {label: 'Unread', value: 'unread'},
  {label: 'Comments', value: 'comments'},
  {label: 'Activity', value: 'activity'},
  {label: 'Messages', value: 'messages'},
];

const notificationHeaderIconStyle = {
  '--notification-header-icon-url': `url("${process.env.PUBLIC_URL}/icons/notification-line.svg")`,
} as React.CSSProperties;

const notificationSummaryIconStyle = {
  '--notification-summary-icon-url': `url("${process.env.PUBLIC_URL}/icons/notification-line.svg")`,
} as React.CSSProperties;

const commentSummaryIconStyle = {
  '--notification-summary-icon-url': `url("${process.env.PUBLIC_URL}/icons/comment-outline.svg")`,
} as React.CSSProperties;

const reactionSummaryIconStyle = {
  '--notification-summary-icon-url': `url("${process.env.PUBLIC_URL}/icons/heart-outline.svg")`,
} as React.CSSProperties;

const messageSummaryIconStyle = {
  '--notification-summary-icon-url': `url("${process.env.PUBLIC_URL}/icons/message.svg")`,
} as React.CSSProperties;

const notificationTypeIconStyle = (fileName: string) => ({
  '--notification-type-icon-url': `url("${process.env.PUBLIC_URL}/icons/${fileName}")`,
} as React.CSSProperties);

const messageTypeIconStyle = notificationTypeIconStyle('message.svg');
const followTypeIconStyle = notificationTypeIconStyle('user-add-fill.svg');
const commentTypeIconStyle = notificationTypeIconStyle('comment-outline.svg');
const reactionTypeIconStyle = notificationTypeIconStyle('heart-outline.svg');
const reelShareTypeIconStyle = notificationTypeIconStyle('share-fill.svg');

const POST_REACTION_NOTIFICATION_COPY: Record<NotificationReactionType, (actorName: string) => string> = {
  like: (actorName) => `${actorName} liked your post 👍`,
  love: (actorName) => `${actorName} loved your post ❤️`,
  celebrate: (actorName) => `${actorName} celebrated your post 🎉`,
  fire: (actorName) => `${actorName} was excited by your post 🔥`,
  laugh: (actorName) => `${actorName} laughed at your post 😂`,
  cry: (actorName) => `${actorName} cried over your post 😭`,
};

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getNotificationDateGroup = (value: string): NotificationDateGroup => {
  const notificationDate = new Date(value);
  if (Number.isNaN(notificationDate.getTime())) {
    return 'Today';
  }

  const today = startOfLocalDay(new Date());
  const itemDay = startOfLocalDay(notificationDate);
  const dayDifference = Math.floor((today.getTime() - itemDay.getTime()) / 86_400_000);

  if (dayDifference <= 0) return 'Today';
  if (dayDifference === 1) return 'Yesterday';

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  return itemDay >= startOfWeek ? 'Earlier this week' : 'Older';
};

const formatNotificationTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';

  const time = new Intl.DateTimeFormat('en-US', {hour: 'numeric', minute: '2-digit'}).format(date);
  const group = getNotificationDateGroup(value);
  if (group === 'Today') return time;
  if (group === 'Yesterday') return `Yesterday, ${time}`;

  const datePart = new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric'}).format(date);
  return `${datePart}, ${time}`;
};

const sortNotifications = (inputNotifications: NotificationRecord[]) => (
  [...inputNotifications].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
);

const mergeNotifications = (
  currentNotifications: NotificationRecord[],
  nextNotifications: NotificationRecord[],
) => {
  const notificationsById = new Map(
    currentNotifications.map((notification) => [notification.id, notification])
  );
  nextNotifications.forEach((notification) => notificationsById.set(notification.id, notification));
  return sortNotifications(Array.from(notificationsById.values()));
};

const getPostReactionNotificationTitle = (notification: NotificationRecord, actorName: string) => {
  const reactionType = notification.metadata?.reactionType || 'like';
  const buildTitle = POST_REACTION_NOTIFICATION_COPY[reactionType];
  return buildTitle ? buildTitle(actorName) : POST_REACTION_NOTIFICATION_COPY.like(actorName);
};

const getNotificationTitle = (notification: NotificationRecord) => {
  const actorName = notification.actor.name || 'Someone';
  switch (notification.type) {
    case 'message': return `${actorName} sent you a message`;
    case 'follow': return `${actorName} followed you`;
    case 'post_comment': return `${actorName} commented on your post`;
    case 'reel_comment': return `${actorName} commented on your reel`;
    case 'reel_share': return `${actorName} shared a reel with you`;
    case 'post_like': return getPostReactionNotificationTitle(notification, actorName);
    case 'post_repost': return `${actorName} reposted your post`;
    default: return `${actorName} interacted with your profile`;
  }
};

const getNotificationPreview = (notification: NotificationRecord) => {
  if (notification.previewText?.trim()) return notification.previewText.trim();
  switch (notification.type) {
    case 'message': return 'You have a new message.';
    case 'follow': return 'Start a conversation!';
    case 'post_comment': return 'They joined the conversation.';
    case 'reel_comment': return 'They commented on your reel.';
    case 'reel_share': return 'Open the reel to review this athlete’s highlights.';
    case 'post_like': return 'Your post is getting love!';
    case 'post_repost': return 'Your post reached a new audience.';
    default: return 'You have new profile activity.';
  }
};

const getNotificationPath = (notification: NotificationRecord) => {
  switch (notification.type) {
    case 'message': return `/messages?conversationId=${encodeURIComponent(notification.target.id)}`;
    case 'follow': return `/profile?userId=${encodeURIComponent(notification.actor.id)}`;
    case 'reel_comment':
    case 'reel_share': return `/home?reel=${encodeURIComponent(notification.target.id)}`;
    case 'post_comment':
    case 'post_like':
    case 'post_repost': return `/home?post=${encodeURIComponent(notification.target.id)}`;
    default: return '/';
  }
};

const getNotificationNavigationState = (notification: NotificationRecord) => {
  switch (notification.type) {
    case 'post_comment':
    case 'post_like':
    case 'post_repost':
      return {focusPostId: notification.target.id, focusSource: 'notification'};
    default:
      return undefined;
  }
};

const notificationMatchesView = (notification: NotificationRecord, view: NotificationView) => {
  switch (view) {
    case 'unread': return !notification.readAt;
    case 'comments': return notification.type === 'post_comment' || notification.type === 'reel_comment';
    case 'activity': return ['follow', 'post_like', 'post_repost'].includes(notification.type);
    case 'messages': return notification.type === 'message';
    default: return true;
  }
};

const NotificationAvatar: React.FC<{notification: NotificationRecord}> = ({notification}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const actorAvatarUrl = getAssetUrl(notification.actor.profilePicture);
  const shouldRenderAvatar = Boolean(actorAvatarUrl) && !imageFailed;

  React.useEffect(() => setImageFailed(false), [actorAvatarUrl]);

  return (
    <span className={styles.notificationAvatar}>
      {shouldRenderAvatar ? (
        <img
          src={actorAvatarUrl}
          alt={`${notification.actor.name} avatar`}
          className={styles.notificationAvatarImage}
          onError={() => setImageFailed(true)}
        />
      ) : getDisplayInitial(notification.actor.name)}
    </span>
  );
};

const NotificationTypeBadge: React.FC<{notification: NotificationRecord}> = ({notification}) => {
  if (notification.type === 'post_like') {
    return (
      <span className={`${styles.typeBadge} ${styles.typeBadgeReaction}`.trim()} aria-hidden="true">
        <span className={styles.typeBadgeIconAsset} style={reactionTypeIconStyle} />
      </span>
    );
  }

  let icon = <span className={styles.typeBadgeIconAsset} style={messageTypeIconStyle} />;
  let colorClass = styles.typeBadgeMessage;
  if (notification.type === 'follow') {
    icon = <span className={styles.typeBadgeIconAsset} style={followTypeIconStyle} />;
    colorClass = styles.typeBadgeFollow;
  } else if (notification.type === 'post_comment') {
    icon = <span className={styles.typeBadgeIconAsset} style={commentTypeIconStyle} />;
    colorClass = styles.typeBadgePostComment;
  } else if (notification.type === 'reel_comment') {
    icon = <span className={styles.typeBadgeIconAsset} style={commentTypeIconStyle} />;
    colorClass = styles.typeBadgeComment;
  } else if (notification.type === 'reel_share') {
    icon = <span className={styles.typeBadgeIconAsset} style={reelShareTypeIconStyle} />;
    colorClass = styles.typeBadgeMessage;
  } else if (notification.type === 'post_repost') {
    icon = <Repeat2 size={18} strokeWidth={2.5} />;
    colorClass = styles.typeBadgeRepost;
  }

  return <span className={`${styles.typeBadge} ${colorClass}`.trim()} aria-hidden="true">{icon}</span>;
};

const Notifications: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
  const {reconnectSequence, socket} = useRealtime();
  const [view, setView] = React.useState<NotificationView>('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState<NotificationSortOrder>('newest');
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  const sortMenuRef = React.useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = React.useState<NotificationRecord[]>([]);
  const [categoryCounts, setCategoryCounts] = React.useState({comments: 0, reactions: 0, messages: 0});
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const apiFilter = view === 'comments' ? 'comments' : 'all';

  const loadNotifications = React.useCallback(async (cursor?: string) => {
    if (!token) {
      setNotifications([]);
      setCategoryCounts({comments: 0, reactions: 0, messages: 0});
      setNextCursor(null);
      setLoading(false);
      setLoadingMore(false);
      setError('You must be signed in to view notifications.');
      return;
    }

    cursor ? setLoadingMore(true) : setLoading(true);
    try {
      const response = await notificationApi.listNotifications(token, {filter: apiFilter, cursor, limit: 20});
      setNotifications((currentNotifications) => (
        cursor ? mergeNotifications(currentNotifications, response.notifications) : sortNotifications(response.notifications)
      ));
      if (response.summary) {
        setCategoryCounts({
          comments: response.summary.commentsCount,
          reactions: response.summary.reactionsCount,
          messages: response.summary.messagesCount,
        });
      } else if (!cursor) {
        const loadedCounts = {
          comments: response.notifications.filter((notification) => notificationMatchesView(notification, 'comments')).length,
          reactions: response.notifications.filter((notification) => notification.type === 'post_like').length,
          messages: response.notifications.filter((notification) => notification.type === 'message').length,
        };
        setCategoryCounts((currentCounts) => (
          apiFilter === 'comments'
            ? {...currentCounts, comments: loadedCounts.comments}
            : loadedCounts
        ));
      }
      setNextCursor(response.nextCursor || null);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load notifications.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [apiFilter, token]);

  React.useEffect(() => { void loadNotifications(); }, [loadNotifications]);

  React.useEffect(() => {
    if (!sortMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!sortMenuRef.current?.contains(event.target as Node)) setSortMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSortMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sortMenuOpen]);

  React.useEffect(() => {
    if (!socket) return;

    const handleNotificationCreated = ({notification}: {notification: NotificationRecord}) => {
      setNotifications((currentNotifications) => mergeNotifications(currentNotifications, [notification]));
      setCategoryCounts((currentCounts) => ({
        comments: currentCounts.comments + (notificationMatchesView(notification, 'comments') ? 1 : 0),
        reactions: currentCounts.reactions + (notification.type === 'post_like' ? 1 : 0),
        messages: currentCounts.messages + (notification.type === 'message' ? 1 : 0),
      }));
    };
    const handleNotificationMarkedRead = (
      payload: {notificationId: string; readAt: string; unreadCount: number}
    ) => {
      setNotifications((currentNotifications) => currentNotifications.map((notification) => (
        notification.id === payload.notificationId ? {...notification, readAt: payload.readAt} : notification
      )));
    };
    const handleNotificationsAllRead = ({readAt}: {readAt: string; unreadCount: number}) => {
      setNotifications((currentNotifications) => currentNotifications.map((notification) => ({
        ...notification,
        readAt: notification.readAt || readAt,
      })));
    };

    socket.on('notifications:created', handleNotificationCreated);
    socket.on('notifications:marked-read', handleNotificationMarkedRead);
    socket.on('notifications:all-read', handleNotificationsAllRead);
    return () => {
      socket.off('notifications:created', handleNotificationCreated);
      socket.off('notifications:marked-read', handleNotificationMarkedRead);
      socket.off('notifications:all-read', handleNotificationsAllRead);
    };
  }, [socket]);

  React.useEffect(() => {
    if (token && reconnectSequence !== 0) void loadNotifications();
  }, [loadNotifications, reconnectSequence, token]);

  const handleNotificationOpen = React.useCallback(async (notification: NotificationRecord) => {
    if (!token) return;

    try {
      if (!notification.readAt) {
        const response = await notificationApi.markAsRead(token, notification.id);
        dispatch(setNotificationUnreadCount(response.summary.unreadCount));
        setNotifications((currentNotifications) => currentNotifications.map((currentNotification) => (
          currentNotification.id === notification.id ? response.notification : currentNotification
        )));
      }
    } catch (markReadError) {
      setError(markReadError instanceof Error ? markReadError.message : 'Failed to update the notification state.');
    }

    const notificationPath = getNotificationPath(notification);
    const navigationState = getNotificationNavigationState(notification);
    if (navigationState) navigate(notificationPath, {state: navigationState});
    else navigate(notificationPath);
  }, [dispatch, navigate, token]);

  const filteredNotifications = React.useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    return notifications
      .filter((notification) => notificationMatchesView(notification, view))
      .filter((notification) => !normalizedQuery || [
        notification.actor.name,
        getNotificationTitle(notification),
        getNotificationPreview(notification),
      ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery)))
      .sort((left, right) => {
        const difference = new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
        return sortOrder === 'newest' ? difference : -difference;
      });
  }, [notifications, searchQuery, sortOrder, view]);

  const groupedNotifications = React.useMemo(() => {
    const groups = new Map<NotificationDateGroup, NotificationRecord[]>();
    filteredNotifications.forEach((notification) => {
      const group = getNotificationDateGroup(notification.createdAt);
      groups.set(group, [...(groups.get(group) || []), notification]);
    });
    return Array.from(groups.entries());
  }, [filteredNotifications]);

  const summaryCards = [
    {
      label: 'Unread',
      value: unreadCount,
      helper: 'New notifications',
      icon: <span className={styles.summaryIconAsset} style={notificationSummaryIconStyle} />,
      colorClass: styles.summaryUnread,
    },
    {
      label: 'Comments',
      value: categoryCounts.comments,
      helper: 'Post and reel comments',
      icon: <span className={styles.summaryIconAsset} style={commentSummaryIconStyle} />,
      colorClass: styles.summaryComments,
    },
    {
      label: 'Reactions',
      value: categoryCounts.reactions,
      helper: 'Likes and reactions',
      icon: <span className={styles.summaryIconAsset} style={reactionSummaryIconStyle} />,
      colorClass: styles.summaryReactions,
    },
    {
      label: 'Messages',
      value: categoryCounts.messages,
      helper: 'New messages',
      icon: <span className={styles.summaryIconAsset} style={messageSummaryIconStyle} />,
      colorClass: styles.summaryMessages,
    },
  ];
  const activeViewLabel = NOTIFICATION_VIEWS.find((option) => option.value === view)?.label || 'All';

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageContent}>
        <div className={styles.pageSectionShell}>
        <header className={styles.pageHeader}>
          <span className={styles.headerIcon} aria-hidden="true">
            <span className={styles.headerIconAsset} style={notificationHeaderIconStyle} />
          </span>
          <div>
            <h1 className={styles.pageTitle}>Notifications</h1>
            <p className={styles.pageSubtitle}>Stay on top of messages, post reactions, reels, and profile activity.</p>
          </div>
        </header>

        <section className={styles.summaryGrid} aria-label="Notification summary">
          {summaryCards.map((card) => (
            <article key={card.label} className={`${styles.summaryCard} ${card.colorClass}`.trim()}>
              <div className={styles.summaryTop}>
                <span className={styles.summaryIcon} aria-hidden="true">{card.icon}</span>
                <div className={styles.summaryCopy}>
                  <span className={styles.summaryLabel}>{card.label}</span>
                  <strong className={styles.summaryValue}>{card.value}</strong>
                </div>
              </div>
              <span className={styles.summaryHelper}>{card.helper}</span>
            </article>
          ))}
        </section>

        <div className={styles.toolbar}>
          <div className={styles.filterRow} role="tablist" aria-label="Notification views">
            {NOTIFICATION_VIEWS.map((filterOption) => (
              <button
                key={filterOption.value}
                type="button"
                role="tab"
                aria-selected={view === filterOption.value}
                className={`${styles.filterButton} ${view === filterOption.value ? styles.filterButtonActive : ''}`.trim()}
                onClick={() => setView(filterOption.value)}
              >
                {filterOption.label}
              </button>
            ))}
          </div>

          <div className={styles.toolbarControls}>
            <label className={styles.searchControl}>
              <svg
                className={styles.searchIcon}
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M14.1667 14.1667L18 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="8.75"
                  cy="8.75"
                  r="5.75"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
              </svg>
              <span className={styles.visuallyHidden}>Search notifications</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search notifications..."
                className={styles.searchInput}
              />
            </label>
            <div className={styles.sortControl} ref={sortMenuRef}>
              <button
                type="button"
                className={styles.sortButton}
                aria-label="Sort notifications"
                aria-haspopup="menu"
                aria-expanded={sortMenuOpen}
                onClick={() => setSortMenuOpen((isOpen) => !isOpen)}
              >
                <span>{sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}</span>
                <ChevronDown className={styles.sortChevron} size={17} aria-hidden="true" />
              </button>
              {sortMenuOpen ? (
                <div className={styles.sortMenu} role="menu" aria-label="Sort notifications">
                  {([
                    {value: 'newest', label: 'Newest first'},
                    {value: 'oldest', label: 'Oldest first'},
                  ] as Array<{value: NotificationSortOrder; label: string}>).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={sortOrder === option.value}
                      className={`${styles.sortOption} ${sortOrder === option.value ? styles.sortOptionSelected : ''}`.trim()}
                      onClick={() => {
                        setSortOrder(option.value);
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>{option.label}</span>
                      {sortOrder === option.value ? <span className={styles.sortCheck} aria-hidden="true" /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {error ? (
          <div className={`${styles.statusMessage} ${styles.statusMessageError}`.trim()} role="alert">
            <span>{error}</span>
            {token ? <button type="button" onClick={() => void loadNotifications()} disabled={loading}>Try again</button> : null}
          </div>
        ) : null}

        <div className={styles.listCard}>
          {loading ? (
            <div className={styles.loadingState}>Loading your notifications...</div>
          ) : filteredNotifications.length === 0 ? (
            <div className={styles.emptyState}>
              <h2 className={styles.emptyStateTitle}>No {activeViewLabel.toLowerCase()} notifications</h2>
              <p className={styles.emptyStateText}>
                {searchQuery.trim()
                  ? 'Try another search or choose a different notification view.'
                  : view === 'unread'
                    ? 'You are all caught up. New activity will appear here.'
                    : 'New activity will appear here as people interact with you.'}
              </p>
            </div>
          ) : (
            <div className={styles.notificationList}>
              {groupedNotifications.map(([groupLabel, groupNotifications]) => {
                const groupId = `notification-group-${groupLabel.split(' ').join('-').toLowerCase()}`;
                return (
                  <section key={groupLabel} className={styles.notificationGroup} aria-labelledby={groupId}>
                    <h2 id={groupId} className={styles.groupTitle}>{groupLabel}</h2>
                    <div className={styles.groupRows}>
                      {groupNotifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          className={`${styles.notificationRow} ${!notification.readAt ? styles.notificationRowUnread : ''}`.trim()}
                          onClick={() => void handleNotificationOpen(notification)}
                        >
                          <span className={styles.avatarCluster}>
                            <NotificationAvatar notification={notification} />
                            <NotificationTypeBadge notification={notification} />
                          </span>
                          <span className={styles.notificationContent}>
                            <span className={styles.notificationTitle}>{getNotificationTitle(notification)}</span>
                            <span className={styles.notificationPreview}>{getNotificationPreview(notification)}</span>
                          </span>
                          <span className={styles.notificationTrailing}>
                            <span className={styles.notificationTimestamp}>{formatNotificationTime(notification.createdAt)}</span>
                            {!notification.readAt ? <span className={styles.notificationUnreadDot} aria-label="Unread" /> : null}
                            <EllipsisVertical className={styles.notificationMore} size={20} aria-hidden="true" />
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>

        {nextCursor ? (
          <div className={styles.loadMoreRow}>
            <button
              type="button"
              className={styles.loadMoreButton}
              onClick={() => void loadNotifications(nextCursor)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
